import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function Cooks() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cooks, setCooks] = useState([]);
  const [loadingCooks, setLoadingCooks] = useState(true);
  const [error, setError] = useState(null);
  const [viewingCook, setViewingCook] = useState(null);
  const [viewingCookProfileUrl, setViewingCookProfileUrl] = useState(null);
  const [editingCook, setEditingCook] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    country: '',
    city: '',
    postal_code: '',
    national_id: '',
    qualification: '',
    experience: '',
    salary: '',
    joining_date: '',
    emergency_contact: '',
    emergency_phone: '',
    is_active: true,
    profile_picture: null
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const [showArchiveConfirmPopup, setShowArchiveConfirmPopup] = useState(false);
  const [cookToArchive, setCookToArchive] = useState(null);
  const [showArchiveSuccessPopup, setShowArchiveSuccessPopup] = useState(false);
  const [archivedCookName, setArchivedCookName] = useState('');
  const [showRestoreConfirmPopup, setShowRestoreConfirmPopup] = useState(false);
  const [cookToRestore, setCookToRestore] = useState(null);
  const [showRestoreSuccessPopup, setShowRestoreSuccessPopup] = useState(false);
  const [restoredCookName, setRestoredCookName] = useState('');
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  const fetchCooks = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingCooks(true);
      let query = supabase
        .from('cooks')
        .select('*')
        .eq('school_id', schoolData.id);
      
      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }
      
      // Filter by active/inactive status based on view mode
      if (viewMode === 'active') {
        query = query.eq('is_active', true);
      } else {
        query = query.eq('is_active', false);
      }
      
      const { data, error } = await query.order('first_name');
      
      if (error) {
        console.error('Error fetching cooks:', error);
        setError('Failed to load cooks');
        return;
      }
      
      setCooks(data || []);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load cooks');
    } finally {
      setLoadingCooks(false);
    }
  };

  useEffect(() => {
    // Fetch cooks for the school
    if (schoolData?.id) {
      fetchCooks();
    }
  }, [schoolData?.id, viewMode, selectedBranchId]);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  const getValidFileUrl = async (url) => {
    if (!url) return null;
    
    try {
      // If it's already a full URL, return it
      if (url.startsWith('http://') || url.startsWith('https://')) {
        // Check if it's a Supabase storage URL
        if (url.includes('/storage/v1/object/')) {
          // Extract bucket and path from URL
          const urlParts = url.split('/storage/v1/object/');
          if (urlParts.length > 1) {
            const pathParts = urlParts[1].split('/');
            const bucket = pathParts[0];
            const filePath = pathParts.slice(1).join('/');
            
            // Generate signed URL
            const { data, error } = await supabase.storage
              .from(bucket)
              .createSignedUrl(filePath, 3600);
            
            if (!error && data) {
              return data.signedUrl;
            }
          }
        }
        return url;
      }
      
      // If it's a relative path, try to extract bucket and path
      // Assuming it's in teacher-uploads bucket (shared with teachers)
      const { data, error } = await supabase.storage
        .from('teacher-uploads')
        .createSignedUrl(url, 3600);
      
      if (!error && data) {
        return data.signedUrl;
      }
      
      return url;
    } catch (err) {
      console.error('Error generating signed URL:', err);
      return url;
    }
  };

  const handleViewCook = async (cook) => {
    setViewingCook(cook);
    setViewingCookProfileUrl(null);
    
    if (cook.profile_picture_url) {
      const signedUrl = await getValidFileUrl(cook.profile_picture_url);
      setViewingCookProfileUrl(signedUrl);
    }
  };

  const closeViewPopup = () => {
    setViewingCook(null);
    setViewingCookProfileUrl(null);
  };

  const handleEditCook = async (cook) => {
    setEditingCook(cook);
    
    setEditForm({
      first_name: cook.first_name || '',
      middle_name: cook.middle_name || '',
      last_name: cook.last_name || '',
      email: cook.email || '',
      phone: cook.phone || '',
      date_of_birth: cook.date_of_birth || '',
      address: cook.address || '',
      country: cook.country || '',
      city: cook.city || '',
      postal_code: cook.postal_code || '',
      national_id: cook.national_id || '',
      qualification: cook.qualification || '',
      experience: cook.experience || '',
      salary: cook.salary || '',
      joining_date: cook.joining_date || '',
      emergency_contact: cook.emergency_contact || '',
      emergency_phone: cook.emergency_phone || '',
      is_active: cook.is_active !== false,
      profile_picture: null
    });
    
    setProfilePicturePreview(null);
    setEditErrors({});
    
    // Load existing profile picture if available
    if (cook.profile_picture_url) {
      const signedUrl = await getValidFileUrl(cook.profile_picture_url);
      setProfilePicturePreview(signedUrl);
    }
  };

  const closeEditPopup = () => {
    setEditingCook(null);
    setEditForm({
      first_name: '',
      middle_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      address: '',
      country: '',
      city: '',
      postal_code: '',
      national_id: '',
      qualification: '',
      experience: '',
      salary: '',
      joining_date: '',
      emergency_contact: '',
      emergency_phone: '',
      is_active: true,
      profile_picture: null
    });
    setProfilePicturePreview(null);
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEditProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setEditForm(prev => ({
      ...prev,
      profile_picture: file
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeEditProfilePicture = () => {
    setEditForm(prev => ({
      ...prev,
      profile_picture: null
    }));
    setProfilePicturePreview(null);
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!editForm.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    
    if (!editForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(editForm.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!editForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!editForm.address.trim()) {
      errors.address = 'Address is required';
    }
    
    if (!editForm.country.trim()) {
      errors.country = 'Country is required';
    }
    
    if (!editForm.city.trim()) {
      errors.city = 'City/Town is required';
    }
    
    if (!editForm.postal_code.trim()) {
      errors.postal_code = 'Postal Code is required';
    }
    
    if (!editForm.qualification.trim()) {
      errors.qualification = 'Cooking certification/qualification is required';
    }
    
    if (!editForm.experience.trim()) {
      errors.experience = 'Experience is required';
    } else if (isNaN(editForm.experience) || parseInt(editForm.experience) < 0) {
      errors.experience = 'Experience must be a valid number';
    }
    
    if (!editForm.salary.trim()) {
      errors.salary = 'Salary is required';
    } else if (isNaN(editForm.salary) || parseFloat(editForm.salary) <= 0) {
      errors.salary = 'Salary must be a valid positive number';
    }
    
    if (!editForm.joining_date.trim()) {
      errors.joining_date = 'Joining date is required';
    }
    
    if (!editForm.emergency_contact.trim()) {
      errors.emergency_contact = 'Emergency contact is required';
    }
    
    if (!editForm.emergency_phone.trim()) {
      errors.emergency_phone = 'Emergency phone is required';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Upload profile picture if a new one was selected
      let profilePictureUrl = editingCook.profile_picture_url;
      if (editForm.profile_picture) {
        try {
          setUploadingPicture(true);
          const fileExt = editForm.profile_picture.name.split('.').pop();
          const fileName = `cook_${editingCook.id}_${Date.now()}.${fileExt}`;
          const filePath = `cook-profiles/${schoolData.id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('teacher-uploads')
            .upload(filePath, editForm.profile_picture, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            alert('Failed to upload profile picture. Continuing with update...');
          } else {
            // Get public URL
            const { data: urlData } = supabase.storage
              .from('teacher-uploads')
              .getPublicUrl(filePath);
            profilePictureUrl = urlData.publicUrl;
          }
        } catch (uploadErr) {
          console.error('Error uploading profile picture:', uploadErr);
        } finally {
          setUploadingPicture(false);
        }
      }
      
      // Update cook data
      const cookData = {
        first_name: editForm.first_name.trim(),
        middle_name: editForm.middle_name.trim() || null,
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        date_of_birth: editForm.date_of_birth,
        phone: editForm.phone.trim(),
        address: editForm.address.trim(),
        country: editForm.country.trim(),
        city: editForm.city.trim(),
        postal_code: editForm.postal_code.trim(),
        qualification: editForm.qualification.trim(),
        experience: parseInt(editForm.experience),
        salary: parseFloat(editForm.salary),
        joining_date: editForm.joining_date,
        emergency_contact: editForm.emergency_contact.trim(),
        emergency_phone: editForm.emergency_phone.trim(),
        national_id: editForm.national_id.trim() || null,
        profile_picture_url: profilePictureUrl,
        is_active: editForm.is_active,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('cooks')
        .update(cookData)
        .eq('id', editingCook.id);
      
      if (error) {
        console.error('Error updating cook:', error);
        alert(`Failed to update cook: ${error.message}`);
        setIsSubmitting(false);
        return;
      }
      
      // Refresh cooks list
      await fetchCooks();
      
      // Close popup
      closeEditPopup();
      
      alert('Cook updated successfully!');
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update cook. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveCook = (cook) => {
    setCookToArchive(cook);
    setShowArchiveConfirmPopup(true);
  };

  const confirmArchiveCook = async () => {
    if (!cookToArchive) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('cooks')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', cookToArchive.id);
      
      if (error) {
        console.error('Error archiving cook:', error);
        alert('Failed to archive cook. Please try again.');
        setIsSubmitting(false);
        setShowArchiveConfirmPopup(false);
        setCookToArchive(null);
        return;
      }

      // Store archived cook name for success popup
      const archivedName = `${cookToArchive.first_name} ${cookToArchive.last_name}`;
      
      // Refresh cooks list
      await fetchCooks();
      setShowArchiveConfirmPopup(false);
      setCookToArchive(null);
      
      // Show success popup
      setArchivedCookName(archivedName);
      setShowArchiveSuccessPopup(true);
    } catch (err) {
      console.error('Error archiving cook:', err);
      alert('Failed to archive cook. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelArchiveCook = () => {
    setShowArchiveConfirmPopup(false);
    setCookToArchive(null);
  };

  const handleRestoreCook = (cook) => {
    setCookToRestore(cook);
    setShowRestoreConfirmPopup(true);
  };

  const confirmRestoreCook = async () => {
    if (!cookToRestore) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('cooks')
        .update({ 
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', cookToRestore.id);
      
      if (error) {
        console.error('Error restoring cook:', error);
        alert('Failed to restore cook. Please try again.');
        setIsSubmitting(false);
        setShowRestoreConfirmPopup(false);
        setCookToRestore(null);
        return;
      }

      // Store restored cook name for success popup
      const restoredName = `${cookToRestore.first_name} ${cookToRestore.last_name}`;
      
      // Refresh cooks list
      await fetchCooks();
      setShowRestoreConfirmPopup(false);
      setCookToRestore(null);
      
      // Show success popup
      setRestoredCookName(restoredName);
      setShowRestoreSuccessPopup(true);
    } catch (err) {
      console.error('Error restoring cook:', err);
      alert('Failed to restore cook. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRestoreCook = () => {
    setShowRestoreConfirmPopup(false);
    setCookToRestore(null);
  };

  const handleDeleteCook = async (cookId, cookName, authUserId) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete ${cookName}?\n\nThis action cannot be undone and will:\n- Delete the cook's record\n- Delete their authentication account\n- Remove all associated data`
    );
    
    if (!confirmDelete) {
      return;
    }

    try {
      // Delete from cooks table (cascade should handle auth user if set up)
      const { error: deleteError } = await supabase
        .from('cooks')
        .delete()
        .eq('id', cookId);
      
      if (deleteError) {
        console.error('Error deleting cook:', deleteError);
        alert('Failed to delete cook. Please try again.');
        return;
      }
      
      // Try to delete auth user if auth_user_id exists
      if (authUserId) {
        try {
          const { error: authError } = await supabase.auth.admin.deleteUser(authUserId);
          if (authError) {
            console.warn('Could not delete auth user:', authError);
          }
        } catch (authErr) {
          console.warn('Error deleting auth user:', authErr);
        }
      }
      
      alert('Cook deleted successfully!');
      await fetchCooks();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete cook. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1>Cooks Management</h1>
                <p>{viewMode === 'active' ? 'View and manage all active cooks in your school' : 'View and manage archived cooks'}</p>
              </div>
            </div>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{cooks.length}</span>
              <span className="stat-label">Total Cooks</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {cooks.filter(cook => cook.is_active).length}
              </span>
              <span className="stat-label">Active Cooks</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">
                {cooks.reduce((total, cook) => total + (parseInt(cook.experience) || 0), 0)}
              </span>
              <span className="stat-label">Total Experience (Years)</span>
            </div>
          </div>

          <div className="students-actions">
            {/* Add Cook Button */}
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => navigate('/school/add-cook')}
                className="submit-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>➕</span>
                <span>Add Cook</span>
              </button>
            </div>
            {/* View Mode Toggle */}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', background: '#F3F4F6', borderRadius: '8px', padding: '0.25rem', width: 'fit-content', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setViewMode('active')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'active' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'active' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'active' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'active' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                👥 Active Cooks
              </button>
              <button
                onClick={() => setViewMode('archived')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'archived' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'archived' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'archived' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'archived' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📦 Archived Cooks
              </button>
            </div>
          </div>

          {loadingCooks ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading cooks...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : cooks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👨‍🍳</div>
              <h3>No {viewMode === 'active' ? 'Active' : 'Archived'} Cooks Found</h3>
              <p>
                {viewMode === 'active' 
                  ? "You haven't added any active cooks yet. Start by adding your first cook!"
                  : "You don't have any archived cooks yet."}
              </p>
              {viewMode === 'active' && (
                <button 
                  className="add-first-teacher-btn"
                  onClick={() => navigate('/school/add-cook')}
                >
                  ➕ Add Your First Cook
                </button>
              )}
            </div>
          ) : (
            <div className="students-table-container">
              <table className="students-table" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>Cook Name</th>
                    <th style={{ width: '25%' }}>Email</th>
                    <th style={{ width: '20%' }}>Qualification</th>
                    <th style={{ width: '15%' }}>Experience</th>
                    <th style={{ width: '20%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cooks.map((cook) => (
                    <tr key={cook.id}>
                      <td style={{ color: '#000000' }}>
                        <strong>{cook.first_name} {cook.middle_name ? cook.middle_name + ' ' : ''}{cook.last_name}</strong>
                      </td>
                      <td className="email-cell">
                        <a href={`mailto:${cook.email}`} className="email-link">
                          {cook.email}
                        </a>
                      </td>
                      <td style={{ color: '#000000' }}>
                        {cook.qualification || '-'}
                      </td>
                      <td style={{ color: '#000000' }}>
                        {cook.experience ? `${cook.experience} years` : '-'}
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          {viewMode === 'active' ? (
                            <>
                              <button
                                className="view-btn"
                                title="View Details"
                                onClick={() => handleViewCook(cook)}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem'
                                }}
                              >
                                👁️
                              </button>
                              <button
                                className="edit-btn"
                                title="Edit Cook"
                                onClick={() => handleEditCook(cook)}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem'
                                }}
                              >
                                ✏️
                              </button>
                              <button
                                className="archive-btn"
                                title="Archive Cook"
                                onClick={() => handleArchiveCook(cook)}
                                disabled={isSubmitting}
                                style={{
                                  background: 'transparent',
                                  color: '#8B5CF6',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem',
                                  opacity: isSubmitting ? 0.5 : 1
                                }}
                              >
                                📦
                              </button>
                              <button
                                className="delete-btn"
                                title="Delete Cook"
                                onClick={() => handleDeleteCook(cook.id, `${cook.first_name} ${cook.last_name}`, cook.auth_user_id)}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem'
                                }}
                              >
                                🗑️
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="unarchive-btn"
                                title="Restore Cook"
                                onClick={() => handleRestoreCook(cook)}
                                disabled={isSubmitting}
                                style={{
                                  background: 'transparent',
                                  color: '#10B981',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem',
                                  opacity: isSubmitting ? 0.5 : 1
                                }}
                              >
                                ♻️
                              </button>
                              <button
                                className="delete-btn"
                                title="Delete Cook"
                                onClick={() => handleDeleteCook(cook.id, `${cook.first_name} ${cook.last_name}`, cook.auth_user_id)}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem'
                                }}
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View Cook Details Popup */}
      {viewingCook && (
        <div className="popup-overlay" onClick={closeViewPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', paddingBottom: '2rem' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Cook Details: {viewingCook.first_name} {viewingCook.middle_name ? viewingCook.middle_name + ' ' : ''}{viewingCook.last_name}
              </h3>
              <button className="close-btn" onClick={closeViewPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB' }}>
              {/* Profile Picture */}
              {viewingCookProfileUrl && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                  <img 
                    src={viewingCookProfileUrl} 
                    alt={`${viewingCook.first_name} ${viewingCook.last_name}`}
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '50%',
                      border: '4px solid #FF8C42',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onError={async (e) => {
                      if (viewingCook.profile_picture_url) {
                        try {
                          const newUrl = await getValidFileUrl(viewingCook.profile_picture_url);
                          if (newUrl && newUrl !== e.target.src) {
                            e.target.src = newUrl;
                          } else {
                            e.target.style.display = 'none';
                          }
                        } catch (err) {
                          e.target.style.display = 'none';
                        }
                      }
                    }}
                  />
                </div>
              )}

              {/* Personal Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Personal Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>First Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingCook.first_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Middle Name</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.middle_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Last Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingCook.last_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Date of Birth</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.date_of_birth ? new Date(viewingCook.date_of_birth).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Employee ID</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.employee_id || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.email ? (
                          <a href={`mailto:${viewingCook.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingCook.email}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.phone ? (
                          <a href={`tel:${viewingCook.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingCook.phone}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Address</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.address || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Country</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.country || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>City</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.city || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Postal Code</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.postal_code || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>National/Government ID</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.national_id || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Professional Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Qualification</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.qualification || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Experience (Years)</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.experience ? `${viewingCook.experience} years` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Salary</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.salary ? `$${parseFloat(viewingCook.salary).toLocaleString()}` : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Joining Date</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingCook.joining_date ? new Date(viewingCook.joining_date).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          background: viewingCook.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingCook.is_active ? '#065F46' : '#991B1B'
                        }}>
                          {viewingCook.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact Card */}
              {(viewingCook.emergency_contact || viewingCook.emergency_phone) && (
                <div className="detail-section" style={{ marginBottom: '2rem' }}>
                  <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                    Emergency Contact
                  </h4>
                  <div style={{
                    padding: '1.5rem',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '2px solid #E5E7EB',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                    }}></div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Contact Name</span>
                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                          {viewingCook.emergency_contact || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Contact Phone</span>
                        <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                          {viewingCook.emergency_phone ? (
                            <a href={`tel:${viewingCook.emergency_phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                              {viewingCook.emergency_phone}
                            </a>
                          ) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Cook Popup */}
      {editingCook && (
        <div className="popup-overlay" onClick={closeEditPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Edit Cook: {editingCook.first_name} {editingCook.middle_name ? editingCook.middle_name + ' ' : ''}{editingCook.last_name}
              </h3>
              <button className="close-btn" onClick={closeEditPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} style={{ padding: '1.5rem' }}>
                <div className="form-section">
                  <h3>Cook Information</h3>
                  <div className="form-row three-columns">
                    <div className="form-group">
                      <label htmlFor="edit_first_name">First Name *</label>
                      <input
                        type="text"
                        id="edit_first_name"
                        name="first_name"
                        value={editForm.first_name}
                        onChange={handleEditInputChange}
                        className={editErrors.first_name ? 'error' : ''}
                        placeholder="Enter first name"
                      />
                      {editErrors.first_name && (
                        <span className="error-text">{editErrors.first_name}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_middle_name">Middle Name</label>
                      <input
                        type="text"
                        id="edit_middle_name"
                        name="middle_name"
                        value={editForm.middle_name}
                        onChange={handleEditInputChange}
                        placeholder="Enter middle name (optional)"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_last_name">Last Name *</label>
                      <input
                        type="text"
                        id="edit_last_name"
                        name="last_name"
                        value={editForm.last_name}
                        onChange={handleEditInputChange}
                        className={editErrors.last_name ? 'error' : ''}
                        placeholder="Enter last name"
                      />
                      {editErrors.last_name && (
                        <span className="error-text">{editErrors.last_name}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                        Profile Picture
                      </label>
                      <div style={{
                        position: 'relative',
                        marginTop: '0.5rem',
                        width: '100%'
                      }}>
                        <input
                          type="file"
                          id="edit_profilePicture"
                          name="profile_picture"
                          accept="image/*"
                          onChange={handleEditProfilePictureChange}
                          style={{
                            position: 'absolute',
                            width: '0.1px',
                            height: '0.1px',
                            opacity: 0,
                            overflow: 'hidden',
                            zIndex: -1,
                            pointerEvents: 'none'
                          }}
                        />
                        <label
                          htmlFor="edit_profilePicture"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0.4rem 0.6rem',
                            background: '#FFFFFF',
                            color: '#374151',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '400',
                            transition: 'all 0.2s ease',
                            width: '100%',
                            textAlign: 'center',
                            gap: '0.5rem',
                            userSelect: 'none',
                            boxSizing: 'border-box'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#F9FAFB';
                            e.currentTarget.style.borderColor = '#9CA3AF';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#D1D5DB';
                          }}
                        >
                          <span style={{ fontSize: '0.9rem' }}>📷</span>
                          <span>{editForm.profile_picture ? 'Change Picture' : 'Choose Picture'}</span>
                        </label>
                      </div>
                      {(editForm.profile_picture || profilePicturePreview || editingCook.profile_picture_url) && (
                        <div style={{
                          marginTop: '1rem',
                          padding: '1rem',
                          background: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}>
                          <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '3px solid #FF8C42',
                            flexShrink: 0,
                            position: 'relative'
                          }}>
                            {profilePicturePreview ? (
                              <img 
                                src={profilePicturePreview} 
                                alt="Profile preview" 
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                  minHeight: '80px',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                                key={profilePicturePreview}
                              />
                            ) : editingCook.profile_picture_url ? (
                              <img 
                                src={editingCook.profile_picture_url} 
                                alt="Profile preview" 
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                  minHeight: '80px',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                                onError={async (e) => {
                                  const signedUrl = await getValidFileUrl(editingCook.profile_picture_url);
                                  if (signedUrl) {
                                    e.target.src = signedUrl;
                                  }
                                }}
                                key={editingCook.profile_picture_url}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                background: '#E5E7EB',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '2rem'
                              }}>
                                👤
                              </div>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            {editForm.profile_picture && (
                              <>
                                <div style={{ 
                                  fontWeight: '600', 
                                  color: '#1F2937', 
                                  marginBottom: '0.25rem', 
                                  wordBreak: 'break-word',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {editForm.profile_picture.name}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                  {(editForm.profile_picture.size / 1024 / 1024).toFixed(2)} MB
                                </div>
                              </>
                            )}
                          </div>
                          {editForm.profile_picture && (
                            <button
                              type="button"
                              onClick={removeEditProfilePicture}
                              style={{
                                padding: '0.4rem 0.6rem',
                                background: '#EF4444',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                width: 'auto',
                                height: 'auto',
                                lineHeight: '1.2'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#DC2626';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#EF4444';
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <small style={{ 
                      display: 'block', 
                      marginTop: '0.5rem', 
                      color: '#6B7280', 
                      fontSize: '0.85rem',
                      fontStyle: 'italic'
                    }}>
                      Max size: 5MB. Accepted formats: JPG, PNG, GIF
                    </small>
                  </div>
                  
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <label htmlFor="edit_national_id" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      National/Government ID
                    </label>
                    <input
                      type="text"
                      id="edit_national_id"
                      name="national_id"
                      value={editForm.national_id}
                      onChange={handleEditInputChange}
                      placeholder="Enter National/Government ID (optional)"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit_email">Email *</label>
                      <input
                        type="email"
                        id="edit_email"
                        name="email"
                        value={editForm.email}
                        onChange={handleEditInputChange}
                        className={editErrors.email ? 'error' : ''}
                        placeholder="cook@example.com"
                      />
                      {editErrors.email && (
                        <span className="error-text">{editErrors.email}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_date_of_birth">Date of Birth *</label>
                      <input
                        type="date"
                        id="edit_date_of_birth"
                        name="date_of_birth"
                        value={editForm.date_of_birth}
                        onChange={handleEditInputChange}
                        className={editErrors.date_of_birth ? 'error' : ''}
                      />
                      {editErrors.date_of_birth && (
                        <span className="error-text">{editErrors.date_of_birth}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit_phone">Phone *</label>
                      <input
                        type="tel"
                        id="edit_phone"
                        name="phone"
                        value={editForm.phone}
                        onChange={handleEditInputChange}
                        className={editErrors.phone ? 'error' : ''}
                        placeholder="+1 (555) 123-4567"
                      />
                      {editErrors.phone && (
                        <span className="error-text">{editErrors.phone}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_address">Address *</label>
                      <input
                        type="text"
                        id="edit_address"
                        name="address"
                        value={editForm.address}
                        onChange={handleEditInputChange}
                        className={editErrors.address ? 'error' : ''}
                        placeholder="Enter street address"
                      />
                      {editErrors.address && (
                        <span className="error-text">{editErrors.address}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-row three-columns">
                    <div className="form-group">
                      <label htmlFor="edit_country">Country *</label>
                      <input
                        type="text"
                        id="edit_country"
                        name="country"
                        value={editForm.country}
                        onChange={handleEditInputChange}
                        className={editErrors.country ? 'error' : ''}
                        placeholder="Enter country"
                      />
                      {editErrors.country && (
                        <span className="error-text">{editErrors.country}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_city">City / Town *</label>
                      <input
                        type="text"
                        id="edit_city"
                        name="city"
                        value={editForm.city}
                        onChange={handleEditInputChange}
                        className={editErrors.city ? 'error' : ''}
                        placeholder="Enter city or town"
                      />
                      {editErrors.city && (
                        <span className="error-text">{editErrors.city}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_postal_code">Postal Code *</label>
                      <input
                        type="text"
                        id="edit_postal_code"
                        name="postal_code"
                        value={editForm.postal_code}
                        onChange={handleEditInputChange}
                        className={editErrors.postal_code ? 'error' : ''}
                        placeholder="Enter postal code"
                      />
                      {editErrors.postal_code && (
                        <span className="error-text">{editErrors.postal_code}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Professional Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit_qualification">Cooking Certification/Qualification *</label>
                      <input
                        type="text"
                        id="edit_qualification"
                        name="qualification"
                        value={editForm.qualification}
                        onChange={handleEditInputChange}
                        className={editErrors.qualification ? 'error' : ''}
                        placeholder="e.g., Food Safety Certificate, Culinary Diploma"
                      />
                      {editErrors.qualification && (
                        <span className="error-text">{editErrors.qualification}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_experience">Experience (Years) *</label>
                      <input
                        type="number"
                        id="edit_experience"
                        name="experience"
                        value={editForm.experience}
                        onChange={handleEditInputChange}
                        className={editErrors.experience ? 'error' : ''}
                        placeholder="e.g., 5"
                        min="0"
                      />
                      {editErrors.experience && (
                        <span className="error-text">{editErrors.experience}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit_salary">Salary *</label>
                      <input
                        type="number"
                        id="edit_salary"
                        name="salary"
                        value={editForm.salary}
                        onChange={handleEditInputChange}
                        className={editErrors.salary ? 'error' : ''}
                        placeholder="e.g., 50000"
                        min="0"
                        step="0.01"
                      />
                      {editErrors.salary && (
                        <span className="error-text">{editErrors.salary}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_joining_date">Joining Date *</label>
                      <input
                        type="date"
                        id="edit_joining_date"
                        name="joining_date"
                        value={editForm.joining_date}
                        onChange={handleEditInputChange}
                        className={editErrors.joining_date ? 'error' : ''}
                      />
                      {editErrors.joining_date && (
                        <span className="error-text">{editErrors.joining_date}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Emergency Contact</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit_emergency_contact">Emergency Contact *</label>
                      <input
                        type="text"
                        id="edit_emergency_contact"
                        name="emergency_contact"
                        value={editForm.emergency_contact}
                        onChange={handleEditInputChange}
                        className={editErrors.emergency_contact ? 'error' : ''}
                        placeholder="Emergency contact name"
                      />
                      {editErrors.emergency_contact && (
                        <span className="error-text">{editErrors.emergency_contact}</span>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit_emergency_phone">Emergency Phone *</label>
                      <input
                        type="tel"
                        id="edit_emergency_phone"
                        name="emergency_phone"
                        value={editForm.emergency_phone}
                        onChange={handleEditInputChange}
                        className={editErrors.emergency_phone ? 'error' : ''}
                        placeholder="+1 (555) 987-6543"
                      />
                      {editErrors.emergency_phone && (
                        <span className="error-text">{editErrors.emergency_phone}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Status</h3>
                  <div className="form-row">
                    <div className="form-group" style={{ width: '100%' }}>
                      <div
                        onClick={() => setEditForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '1rem 1.5rem',
                          cursor: 'pointer',
                          borderRadius: '10px',
                          background: editForm.is_active ? '#D1FAE5' : '#FEE2E2',
                          border: editForm.is_active ? '2px solid #10B981' : '2px solid #EF4444',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (editForm.is_active) {
                            e.currentTarget.style.background = '#A7F3D0';
                            e.currentTarget.style.borderColor = '#059669';
                          } else {
                            e.currentTarget.style.background = '#FECACA';
                            e.currentTarget.style.borderColor = '#DC2626';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (editForm.is_active) {
                            e.currentTarget.style.background = '#D1FAE5';
                            e.currentTarget.style.borderColor = '#10B981';
                          } else {
                            e.currentTarget.style.background = '#FEE2E2';
                            e.currentTarget.style.borderColor = '#EF4444';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '6px',
                            background: editForm.is_active ? '#10B981' : '#EF4444',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {editForm.is_active ? (
                              <span style={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 'bold' }}>✓</span>
                            ) : (
                              <span style={{ color: '#FFFFFF', fontSize: '1rem', fontWeight: 'bold' }}>✕</span>
                            )}
                          </div>
                          <span style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: editForm.is_active ? '#065F46' : '#991B1B'
                          }}>
                            {editForm.is_active ? 'Active Cook' : 'Inactive Cook'}
                          </span>
                        </div>
                        <div style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '8px',
                          background: editForm.is_active ? '#10B981' : '#EF4444',
                          color: '#FFFFFF',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          {editForm.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeEditPopup}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting || uploadingPicture}>
                  {uploadingPicture ? 'Uploading Picture...' : isSubmitting ? 'Updating...' : 'Update Cook'}
                </button>
              </div>
            </form>
        </div>
      </div>
      )}

      {/* Archive Confirmation Popup */}
      {showArchiveConfirmPopup && cookToArchive && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelArchiveCook}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>📦</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Archive Cook?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to archive <strong>{cookToArchive.first_name} {cookToArchive.last_name}</strong>?
              <br /><br />
              The cook will be marked as inactive and hidden from the active cooks list. They can be restored later if needed.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelArchiveCook} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmArchiveCook} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)' }}>
                {isSubmitting ? 'Archiving...' : 'Archive Cook'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Success Popup */}
      {showArchiveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowArchiveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Cook Archived Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{archivedCookName}</strong> has been archived successfully.
              <br />
              They are now marked as inactive.
            </p>
            <button onClick={() => setShowArchiveSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Restore Confirmation Popup */}
      {showRestoreConfirmPopup && cookToRestore && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelRestoreCook}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>♻️</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Restore Cook?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to restore <strong>{cookToRestore.first_name} {cookToRestore.last_name}</strong>?
              <br /><br />
              The cook will be marked as active and will appear in the active cooks list.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelRestoreCook} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmRestoreCook} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
                {isSubmitting ? 'Restoring...' : 'Restore Cook'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Success Popup */}
      {showRestoreSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRestoreSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Cook Restored Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{restoredCookName}</strong> has been restored successfully.
              <br />
              The cook is now marked as active.
            </p>
            <button onClick={() => setShowRestoreSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default Cooks;

